import moment from 'moment';
import { createAction, handleActions } from 'redux-actions';
import { push } from 'connected-react-router';

import { offer } from '../services/api';
import { correctId } from '../services/OfferService';
import { AnalyticsService } from '../services/AnalyticsService';

export const receiveOffers = createAction('OFFERS_SUCCESS', (data) => data);
export const receivePastOffers = createAction('OFFERS_PAST_SUCCESS', (data) => data);
export const requestOffers = createAction('OFFERS_REQUEST');
export const changeType = createAction('CHANGE_OFFER_TYPE', (data) => data);
export const cleanOffers = createAction('OFFERS_EMPTYUP');
export const receivePeriods = createAction('PERIODS_SUCCESS');
export const canceledGroup = createAction('CANCELED_OFFER_GROUP', (data) => data);
export const canceledOffer = createAction('CANCELED_OFFER', (data) => data);
export const checkedIn = createAction('ATTENDEE_CHECKED_IN', (data) => data);
export const canceledClaim = createAction('CANCELED_CLAIM', (data) => data);
const cleanUpOffers = createAction('CLEAN_UP_OFFERS', (data) => data);

interface PastPeriods {
  [key: string]: Array<Offer>;
}

export interface State {
  data: Array<Offer>;
  periods: Array<any>;
  loading: boolean;
  past_offers: PastPeriods;
  periodsLoaded: boolean;
  type: 'active' | 'past' | 'upcoming';
}

const initial: State = {
  data: [],
  type: 'active',
  loading: false,
  periods: [],
  past_offers: {},
  periodsLoaded: false,
};

export function loadOffers(businessId: string, type: string) {
  return async (dispatch: Function) => {
    dispatch(cleanOffers());
    dispatch(requestOffers());
    const json: any = await offer.getOffers(businessId, type, {});
    const data = json.data;
    dispatch(receiveOffers({ data, type }));
  };
}

export function loadPastOffers(businessId: string, type: string, period: string) {
  return async (dispatch: Function) => {
    dispatch(requestOffers());
    const json: any = await offer.getOffers(businessId, type, { period });
    dispatch(receivePastOffers({ [period]: json.data }));
  };
}

export function cancelGroup(dealId: string, groupId: string) {
  return (dispatch: Function) =>
    offer.cancelGroup(groupId).then((json) => {
      dispatch(canceledGroup({ dealId, groupId }));
    });
}

export function cancelOffer(deal: Offer, type: string) {
  return (dispatch: Function) => {
    const key = deal.id + deal.template_id + deal.template_key + deal.subtitle;
    if (deal.resource_type === 'deal') {
      if (deal.state === 'upcoming') {
        offer.cancelUpcomingOffer(deal.template_id, deal.template_key || '').then((json) => {
          dispatch(canceledOffer(key));
        });
      } else {
        offer.cancelOffer(deal.id).then((json) => {
          dispatch(canceledOffer(key));
        });
      }
    } else if (deal.resource_type === 'projected_deal') {
      offer.cancelProjectedOffer(deal.template_id, deal.id).then((json) => {
        dispatch(canceledOffer(key));
      });
    }
  };
}

export function cancelClaim(dealId, claimId: string, businessId: string, type: OfferListType) {
  return (dispatch: Function) =>
    offer.cancelClaim(dealId, claimId).then((json) => {
      offer.getOffers(businessId, type, {}).then((json: any) => {
        const data: Array<Offer> = json.data;
        dispatch(receiveOffers({ data, type }));
      });
    });
}

export function loadPeriods(businessId: string) {
  return (dispatch: Function) => {
    offer.getPeriods(businessId).then((data) => {
      dispatch(receivePeriods(data.data));
    });
  };
}

export function changePage(type: string) {
  return (dispatch: Function) => {
    AnalyticsService.track(`${type.capitalize()} Offers tap`, {});
    dispatch(cleanUpOffers());
    dispatch(push(`/offers/${type}`));
  };
}

export default handleActions(
  {
    [requestOffers](state: State): State {
      return { ...state, loading: true };
    },
    [receiveOffers](state: State, { payload }): State {
      if (payload.type !== state.type) return state;
      const data = { ...state, data: payload.data.map(correctId), loading: false };
      return data;
    },
    [changeType](state: State, { payload }): State {
      return { ...state, type: payload };
    },
    [receivePastOffers](state: State, { payload }): State {
      return { ...state, past_offers: { ...state.past_offers, ...payload }, loading: false };
    },
    [cleanOffers](state: State): State {
      return { ...state, loading: false, data: [] };
    },
    [receivePeriods](state: State, { payload }): State {
      return { ...state, periods: payload, periodsLoaded: true };
    },
    [canceledGroup](state: State, { payload }): State {
      let data = [...state.data];
      const offer = data.filter((offer) => offer.id === payload.dealId)[0];
      const oldOffers = data.filter((offer) => offer.id !== payload.dealId);
      if (offer.groups) {
        const group = offer.groups.filter((group) => group.id === payload.groupId)[0];
        const oldGroups = offer.groups.filter((group) => group.id !== payload.groupId);
        const newGroup = { ...group, status: 'cancelled' as const };
        const newOffer = { ...offer, groups: [...oldGroups, newGroup] };
        data = [...oldOffers, newOffer];
      }
      return { ...state, data };
    },
    [checkedIn](state: State, { payload }): State {
      let data = [...state.data];
      const offer = data.filter((offer) => offer.id === payload.dealId)[0];
      const attendee = offer.attendees.filter((attendee) => attendee.id === payload.claimId)[0];
      attendee.status = 'used';
      attendee.checked_in_at = moment().toString();
      return { ...state, data };
    },
    [canceledOffer](state: State, { payload }): State {
      return {
        ...state,
        data: state.data.map((offer) => {
          if (
            payload ===
            (offer.id || offer.template_key) +
              offer.template_id +
              offer.template_key +
              offer.subtitle
          ) {
            return {
              ...offer,
              canceled_at: moment().toString(),
              state: 'canceled',
              attendees: offer.attendees
                ? offer.attendees.map((at) => ({ ...at, canceled_at: moment().format() }))
                : [],
            };
          } else return offer;
        }),
      };
    },
    [canceledClaim](state: State, { payload }): State {
      let data = [...state.data];
      const offer = data.filter((offer) => offer.id === payload.dealId)[0];

      if (offer.type === 'reservation_required') {
        offer.attendees = offer.attendees.map((at) =>
          at.id === payload.claimId ? { ...at, canceled_at: moment().format() } : at
        );
      } else {
        offer.attendees = offer.attendees.filter((attendee) => attendee.id !== payload.claimId);
      }
      return { ...state, data };
    },
    [cleanUpOffers](state: State, { payload }): State {
      return { ...state, data: [] };
    },
  },
  initial
);
